import type { StructureType } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexKey, parseHexKey, type Hex } from '../hex.ts';
import { footprint, isBig } from '../land.ts';
import { isBuildableCell } from './core.ts';
import type { CampaignState, CellState } from './types.ts';

/*
 * Three-cell buildings on terraced land (docs/CONSTRUCCION.md §3). The building lives on its own cell, like any
 * other; the two more it takes hold a `part` pointing back to it, so nothing else goes there and a command on
 * either lands on the building.
 */

/** The cell a command on (q, r) is about: the building's own when (q, r) is one of the cells it takes. */
export const anchorOf = (s: CampaignState, q: number, r: number): Hex => {
  const part = s.cells[hexKey({ q, r })]?.part;
  return part ? parseHexKey(part) : { q, r };
};

/** The cells what stands (or is planned) on `key` takes: its own first. */
export const cellsOf = (s: CampaignState, key: string): Hex[] => {
  const h = parseHexKey(key);
  const c = s.cells[key];
  const type = c?.built?.structure ?? c?.job?.structure;
  if (!s.map.land || !type || !isBig(type)) return [h];
  return footprint(type, h.q, h.r, c?.built?.turn ?? c?.job?.turn ?? 0);
};

/**
 * Why `type` can't go on (q, r) turned `turn`, or null when it can: every cell it takes free and open to
 * building, and a big one's all on one level.
 */
export const placeFail = (s: CampaignState, type: StructureType, q: number, r: number, turn: number): 'notBuildable' | 'occupied' | 'uneven' | null => {
  const cells = s.map.land ? footprint(type, q, r, turn) : [{ q, r }];
  const land = s.map.land;
  const R = s.map.radius;
  const level = land ? land.level[cellId(R, q, r)] : 0;
  for (const h of cells) {
    if (!isBuildableCell(s, h.q, h.r)) return 'notBuildable';
    if (s.cells[hexKey(h)]) return 'occupied';
    if (land && land.level[cellId(R, h.q, h.r)] !== level) return 'uneven';
  }
  return null;
};

/** Marks the other cells the building on `key` takes. */
export const placeParts = (s: CampaignState, key: string): void => {
  for (const h of cellsOf(s, key).slice(1)) s.cells[hexKey(h)] = { built: null, job: null, part: key };
};

/** Frees the other cells the building on `key` took (call before it goes). */
export const clearParts = (s: CampaignState, key: string): void => {
  for (const h of cellsOf(s, key).slice(1)) if (s.cells[hexKey(h)]?.part === key) delete s.cells[hexKey(h)];
};

/** Whether a cell only holds a part of a bigger building. */
export const isPart = (c: CellState | undefined): boolean => !!c?.part;
